import { openArcade } from './arcade/index';
import { isMuted, discover, stopAudio } from './arcade/shared';
import { initDossier } from './dossier';
import "regenerator-runtime/runtime";

// Self-hosted typewriter fonts (bundled by Parcel -> served from 'self', CSP-safe).
import "@fontsource/special-elite/400.css";
import "@fontsource/courier-prime/400.css";
import "@fontsource/courier-prime/700.css";
import "@fontsource/courier-prime/400-italic.css";

// ============================================================================
// CONSTANTS
// ============================================================================
const CLICK_CONFETTI = 5;   // random confetti burst
const CLICK_STARS = 10;     // star confetti + monkey spin
const CLICK_CANNONS = 15;   // side-cannon confetti
const CLICK_PARTY = 20;     // full party mode (resets counter)

const ACTIVE_PULSE_MS = 300;
const DANCE_MS = 1000;
const PULSE_MS = 500;
const DAYS_COUNTER_INTERVAL_MS = 5000;

// Party mode: the visuals used to run for 4.5 min (a units bug). Keep it short.
const PARTY_DURATION_MS = 12000;
const TITLE_FLASH_MS = 5000;
const CONFETTI_BURST_MS = 5000;
const CONFETTI_REPEAT_MS = 5000;

const CONFETTI_COLORS = ['#FFE400', '#FFBD00', '#E89400', '#FFCA6C', '#FDFFB8', '#a786ff', '#fd8bbc', '#eca184', '#f8deb1'];

const FUNNY_ITEMS = [
  {
    "text": "Opens a new tab to look for the tab he just opened",
    "severity": "extreme"
  },
  {
    "text": "Schedules a meeting to find out why there are so many meetings",
    "severity": "high"
  },
  {
    "text": "Has a strategic partnership with the snooze button",
    "severity": "critical"
  },
  {
    "text": "Treats the office like a drive-through",
    "severity": "high"
  },
  {
    "text": "His 30-minute workday includes a 25-minute warm-up",
    "severity": "extreme"
  },
  {
    "text": "Says “quick question” and clears the afternoon",
    "severity": "high"
  },
  {
    "text": "Accepted a sales demo while trying to unsubscribe",
    "severity": "critical"
  },
  {
    "text": "Has 47 urgent emails marked as unread for later",
    "severity": "extreme"
  },
  {
    "text": "The follow-up needs a follow-up",
    "severity": "high"
  },
  {
    "text": "Calls it gas-lamping. Investigation ongoing.",
    "severity": "extreme"
  },
  {
    "text": "Replied “sounds good” without opening the attachment",
    "severity": "high"
  },
  {
    "text": "Chrome asked HIM to restore its previous session",
    "severity": "critical"
  },
  {
    "text": "Listed “available” as a theoretical status",
    "severity": "medium"
  },
  {
    "text": "Put the task on a list of lists to make",
    "severity": "high"
  },
  {
    "text": "Went on mute. Spiritually. For the whole meeting.",
    "severity": "extreme"
  },
  {
    "text": "His calendar and reality are no longer on speaking terms",
    "severity": "critical"
  },
  {
    "text": "The sales rep has him listed as an emergency contact",
    "severity": "high"
  },
  {
    "text": "Says “let me circle back” from an undisclosed location",
    "severity": "medium"
  }
];
let complaintDeck = [];

const KONAMI_CODE = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];

// Tiny base64 blip reused for list-item hover feedback.
const HOVER_SOUND_SRC = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhDC16yetu1E0';

// ============================================================================
// LAZY-LOADED EFFECT LIBRARIES
// ============================================================================
let confettiModulePromise = null;
let balloonsModulePromise = null;

function loadConfetti() {
  if (!confettiModulePromise) {
    confettiModulePromise = import('canvas-confetti').then(m => m.default || m);
  }
  return confettiModulePromise;
}

async function triggerBalloonsAnimation() {
  if (isReducedMotion) return;
  try {
    if (!balloonsModulePromise) {
      balloonsModulePromise = import('balloons-js');
    }
    const mod = await balloonsModulePromise;
    const fn = mod.balloons || mod.default?.balloons || mod.default;
    if (typeof fn === 'function') {
      fn();
    } else {
      throw new Error('balloons function not found in module');
    }
  } catch {
    createProfessionalBalloons();
  }
}

// ============================================================================
// DOM ELEMENTS & ENVIRONMENT
// ============================================================================
const monkeyImage = document.getElementById('monkey');
const chucklesImage = document.getElementById('chuckles');
const chucklesAudio = document.getElementById('chuckles-audio'); // clown.mp3 — plays for the Chuckles exhibit
const fiestaAudio = document.getElementById('fiesta');           // fiesta.mp3 — plays for the monkey exhibit

const isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const isMobileViewport = window.matchMedia('(max-width: 767px)').matches;

let clickCount = 0;
let cursorRotation = 0;
let partyActive = false;
let partyModeType = 'confetti'; // cycles: 'confetti' -> 'balloons' -> 'rainbow'

function scaleParticles(base) {
  if (isReducedMotion) return 0;
  return Math.max(5, Math.round(base * (isMobileViewport ? 0.5 : 1)));
}

function playFiestaAudio() {
  if (!fiestaAudio) return;
  fiestaAudio.muted = isMuted();
  fiestaAudio.currentTime = 0;
  fiestaAudio.play().catch(() => {});
}

// Make the findings dance whenever the Chuckles audio plays.
if (chucklesAudio) {
  chucklesAudio.addEventListener('play', () => {
    document.querySelectorAll('#ed-list li').forEach((li, index) => {
      setTimeout(() => {
        li.classList.add('dance');
        setTimeout(() => li.classList.remove('dance'), DANCE_MS);
      }, index * 100);
    });
  });
}

// ============================================================================
// PAGE INITIALIZATION
// ============================================================================
document.addEventListener('DOMContentLoaded', () => {
  initListItems();
  initKonamiCode();
  initMonkey();
  initChuckles();
  initAddItem();
  initDaysCounter();
  initCursorRotation();
  initGalagaButton();
  initLeverSwitch();
  initDossier();
});

const listItems = () => document.querySelectorAll('#ed-list li');

function wireHoverSound(item, hoverSound) {
  item.addEventListener('mouseenter', () => {
    hoverSound.muted = isMuted();
    hoverSound.currentTime = 0;
    hoverSound.play().catch(() => {});
    item.classList.add('hover-effect');
  });
  item.addEventListener('mouseleave', () => item.classList.remove('hover-effect'));
}

const hoverSound = new Audio(HOVER_SOUND_SRC);
hoverSound.volume = 0.1;

function initListItems() {
  listItems().forEach((item, index) => {
    item.style.animationDelay = `${index * 0.1}s`;
    item.classList.add('slide-in');
    wireHoverSound(item, hoverSound);
  });
}

function initKonamiCode() {
  let konamiIndex = 0;
  const arrowKeyMap = { Up: 'ArrowUp', Down: 'ArrowDown', Left: 'ArrowLeft', Right: 'ArrowRight' };

  document.addEventListener('keydown', (e) => {
    // Ignore while typing or while the game owns the keyboard.
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || window.galagaGameActive) {
      return;
    }

    let key = arrowKeyMap[e.key] || e.key;
    if (typeof key === 'string' && key.length === 1) key = key.toLowerCase();

    if (key === KONAMI_CODE[konamiIndex]) {
      konamiIndex++;
      if (konamiIndex === KONAMI_CODE.length) {
        activatePartyMode();
        konamiIndex = 0;
      }
    } else {
      // A wrong key restarts the sequence (but a repeated first key keeps it alive).
      konamiIndex = key === KONAMI_CODE[0] ? 1 : 0;
    }
  });
}

function initMonkey() {
  if (!monkeyImage) return;
  const clickCounter = document.getElementById('click-counter');

  const triggerMonkey = () => {
    discover('monkey');
    clickCount++;
    if (clickCounter) {
      clickCounter.textContent = `Surveillance triggers: ${clickCount}`;
      clickCounter.style.color = `hsl(${clickCount * 10}, 70%, 50%)`;
    }

    monkeyImage.classList.add('active');
    playFiestaAudio();
    setTimeout(() => monkeyImage.classList.remove('active'), ACTIVE_PULSE_MS);

    if (clickCount === CLICK_CONFETTI) triggerRandomConfetti();

    if (clickCount === CLICK_STARS) {
      triggerStarsConfetti();
      monkeyImage.style.animation = 'spin 1s ease-in-out';
      setTimeout(() => { monkeyImage.style.animation = ''; }, 1000);
    }

    if (clickCount === CLICK_CANNONS) triggerSideCannonConfetti();

    if (clickCount === CLICK_PARTY) {
      activatePartyMode();
      clickCount = 0;
    }
  };

  monkeyImage.addEventListener('click', triggerMonkey);
  monkeyImage.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      triggerMonkey();
    }
  });
}

function initChuckles() {
  if (!chucklesImage || !chucklesAudio) return;

  const triggerChuckles = () => {
    discover('chuckles');
    chucklesAudio.muted = isMuted();
    chucklesImage.classList.add('active');
    chucklesAudio.currentTime = 0;
    chucklesAudio.play().catch(() => {});
    setTimeout(() => chucklesImage.classList.remove('active'), ACTIVE_PULSE_MS);
  };

  chucklesImage.addEventListener('click', triggerChuckles);
  chucklesImage.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      triggerChuckles();
    }
  });
}

function initAddItem() {
  const addItemBtn = document.getElementById('add-item-btn');
  const edList = document.getElementById('ed-list');
  const issueCount = document.getElementById('issue-count');
  if (!addItemBtn || !edList) return;

  addItemBtn.addEventListener('click', () => {
    discover('complaint');
    if (!complaintDeck.length) {
      complaintDeck = [...FUNNY_ITEMS];
      for (let i = complaintDeck.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [complaintDeck[i], complaintDeck[j]] = [complaintDeck[j], complaintDeck[i]]; }
    }
    const randomItem = complaintDeck.pop();
    const newLi = document.createElement('li');
    newLi.setAttribute('data-severity', randomItem.severity);
    newLi.textContent = randomItem.text;
    newLi.title = `Evidence #215-${String(edList.children.length + 1).padStart(3, '0')} • Investigator: concerned coworker`;
    newLi.style.animationDelay = '0s';
    newLi.classList.add('slide-in');
    edList.appendChild(newLi);
    document.getElementById('dossier-status').textContent = `Complaint filed: ${randomItem.text}`;

    if (issueCount) {
      issueCount.textContent = edList.children.length;
      issueCount.style.animation = `pulse ${PULSE_MS}ms`;
      setTimeout(() => { issueCount.style.animation = ''; }, PULSE_MS);
    }

    wireHoverSound(newLi, hoverSound);
  });
}

function initDaysCounter() {
  const daysCounter = document.getElementById('days-counter');
  if (!daysCounter) return;

  const incidents = ['Ed opened another tab.', 'A sales demo was accepted.', 'The meeting started. Ed did not.', 'A Slack message entered witness protection.', 'Ed is circling back. Location unknown.'];
  let incident = 0;
  setInterval(() => {
    if (document.hidden || window.galagaGameActive) return;
    daysCounter.textContent = '0.99';
    document.getElementById('incident-note').textContent = 'Almost a whole day. Stand by…';
    setTimeout(() => {
      daysCounter.textContent = '0';
      daysCounter.style.animation = `shake ${PULSE_MS}ms`;
      document.getElementById('incident-note').textContent = incidents[incident++ % incidents.length];
      setTimeout(() => { daysCounter.style.animation = ''; }, PULSE_MS);
    }, 1800);
  }, 18000);
}

function initCursorRotation() {
  // Motion gimmick — skip entirely for reduced-motion users.
  if (isReducedMotion) return;
  const mainContainer = document.querySelector('.container');
  if (!mainContainer) return;

  // Compute the container rect lazily on click (no per-mousemove layout reads).
  document.addEventListener('click', (e) => {
    const rect = mainContainer.getBoundingClientRect();
    const outside = e.clientX < rect.left || e.clientX > rect.right ||
                    e.clientY < rect.top || e.clientY > rect.bottom;
    if (outside) {
      cursorRotation += 45;
      updateCursor();
    }
  });
}

function initGalagaButton() {
  const galagaBtn = document.getElementById('galaga-btn');
  if (galagaBtn) galagaBtn.addEventListener('click', () => openArcade());
}

// The declassify lever fires the same party mode as the Konami code.
function initLeverSwitch() {
  const lever = document.getElementById('declassify-lever');
  if (!lever) return;

  lever.addEventListener('change', () => {
    if (!lever.checked) return;
    discover('lever');
    activatePartyMode();
    // Snap the lever back to OFF once the party ends, so it can be pulled again.
    setTimeout(() => { lever.checked = false; }, PARTY_DURATION_MS);
  });
}

// ============================================================================
// PARTY MODE (re-entry guarded so triggers can't stack)
// ============================================================================
function activatePartyMode() {
  if (partyActive) return; // ignore re-triggers while a party is already running
  partyActive = true;

  document.body.classList.add('party-mode');
  playFiestaAudio();

  const currentMode = partyModeType;
  if (partyModeType === 'confetti') {
    triggerFireworksConfetti();
    partyModeType = 'balloons';
  } else if (partyModeType === 'balloons') {
    triggerBalloonsAnimation();
    partyModeType = 'rainbow';
  } else {
    triggerRainbowMode();
    partyModeType = 'confetti';
  }

  // Rainbow mode styles the title itself; other modes get a temporary flash.
  const h1 = document.querySelector('h1');
  if (h1 && currentMode !== 'rainbow') {
    h1.classList.add('rainbow-text');
    setTimeout(() => h1.classList.remove('rainbow-text'), TITLE_FLASH_MS);
  }

  setTimeout(() => {
    document.body.classList.remove('party-mode');
    partyActive = false;
    fiestaAudio?.pause();
  }, PARTY_DURATION_MS);
}

async function triggerFireworksConfetti() {
  const confetti = await loadConfetti();
  if (isReducedMotion) return;
  const startTime = Date.now();
  const defaults = {
    startVelocity: 30,
    spread: 360,
    ticks: isMobileViewport ? 40 : 60,
    zIndex: 0,
    colors: CONFETTI_COLORS
  };
  const randomInRange = (min, max) => Math.random() * (max - min) + min;

  const createConfettiBurst = () => {
    const burstEnd = Date.now() + CONFETTI_BURST_MS;
    const interval = setInterval(() => {
      const timeLeft = burstEnd - Date.now();
      if (timeLeft <= 0) return clearInterval(interval);

      const particleCount = scaleParticles(50 * (timeLeft / CONFETTI_BURST_MS));
      if (particleCount <= 0) return;

      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
    }, 250);
  };

  createConfettiBurst();

  const repeatTimer = setInterval(() => {
    if (Date.now() - startTime >= PARTY_DURATION_MS) {
      clearInterval(repeatTimer);
      return;
    }
    createConfettiBurst();
  }, CONFETTI_REPEAT_MS);
}

async function triggerSideCannonConfetti() {
  const confetti = await loadConfetti();
  if (isReducedMotion) return;
  const end = Date.now() + 3000;
  const colors = ['#a786ff', '#fd8bbc', '#eca184', '#f8deb1'];

  const frame = () => {
    if (Date.now() > end) return;
    const count = scaleParticles(2);
    if (count > 0) {
      confetti({ particleCount: count, angle: 60, spread: 55, startVelocity: 60, origin: { x: 0, y: 0.5 }, colors });
      confetti({ particleCount: count, angle: 120, spread: 55, startVelocity: 60, origin: { x: 1, y: 0.5 }, colors });
    }
    requestAnimationFrame(frame);
  };
  frame();
}

async function triggerStarsConfetti() {
  const confetti = await loadConfetti();
  if (isReducedMotion) return;
  const defaults = {
    spread: 360,
    ticks: isMobileViewport ? 35 : 50,
    gravity: 0,
    decay: 0.94,
    startVelocity: 30,
    colors: ['#FFE400', '#FFBD00', '#E89400', '#FFCA6C', '#FDFFB8']
  };

  const shoot = () => {
    const c1 = scaleParticles(40);
    const c2 = scaleParticles(10);
    if (c1 > 0) confetti({ ...defaults, particleCount: c1, scalar: 1.2, shapes: ['star'] });
    if (c2 > 0) confetti({ ...defaults, particleCount: c2, scalar: 0.75, shapes: ['circle'] });
  };

  setTimeout(shoot, 0);
  setTimeout(shoot, 100);
  setTimeout(shoot, 200);
}

async function triggerRandomConfetti() {
  const confetti = await loadConfetti();
  if (isReducedMotion) return;
  const count = scaleParticles(100);
  if (count > 0) confetti({
    angle: Math.random() * 360,
    spread: 55,
    particleCount: count,
    origin: { x: 0.5, y: 0.8 },
    colors: ['#FFE400', '#FFBD00', '#E89400', '#FFCA6C', '#FDFFB8', '#a786ff', '#fd8bbc']
  });
}

// CSS-only 3D balloon fallback when the balloons library is unavailable.
function createProfessionalBalloons() {
  const container = document.createElement('div');
  container.className = 'professional-balloons-container';
  container.style.cssText = `
    position: fixed; inset: 0; z-index: 999; pointer-events: none;
    perspective: 1500px; perspective-origin: 50vw 100vh; overflow: hidden;
  `;
  document.body.appendChild(container);

  const colorPairs = [
    ['#ffec37', '#f8b13d'],
    ['#f89640', '#c03940'],
    ['#3bc0f0', '#0075bc'],
    ['#b0cb47', '#3d954b'],
    ['#cf85b8', '#a3509d'],
  ];

  const amount = Math.max(7, Math.round(window.innerWidth / 120));

  for (let i = 0; i < amount; i++) {
    const [balloonColor, lightColor] = colorPairs[Math.floor(Math.random() * colorPairs.length)];
    const balloon = document.createElement('div');
    balloon.className = 'professional-balloon';

    const size = 80 + Math.random() * 40;
    const x = Math.random() * window.innerWidth;
    const z = -Math.random() * 800;
    const blur = Math.abs(z) / 100;

    balloon.style.cssText = `
      position: absolute; left: ${x}px; top: 100vh;
      width: ${size}px; height: ${size * 1.3}px;
      background: linear-gradient(135deg, ${balloonColor} 0%, ${lightColor} 100%);
      border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
      transform: translateX(-50%) translateZ(${z}px);
      filter: blur(${blur}px);
      z-index: ${Math.floor(1000 - Math.abs(z))};
      pointer-events: none; --z: ${z}px;
      box-shadow: inset -10px -10px 20px rgba(0,0,0,0.3), inset 15px 15px 30px rgba(255,255,255,0.6), 0 10px 30px rgba(0,0,0,0.2);
      animation: professional-balloon-float ${8 + Math.random() * 4}s ease-out forwards;
      animation-delay: ${i * 200}ms;
    `;

    const highlight = document.createElement('div');
    highlight.style.cssText = `
      position: absolute; top: 15%; left: 25%; width: 25%; height: 35%;
      background: radial-gradient(ellipse at center, rgba(255,255,255,0.8) 0%, transparent 70%);
      border-radius: 50%; transform: rotate(-25deg);
    `;
    balloon.appendChild(highlight);

    const string = document.createElement('div');
    string.style.cssText = `
      position: absolute; left: 50%; top: 100%; width: 2px; height: 30px;
      background: linear-gradient(to bottom, #666, #333); transform: translateX(-50%); border-radius: 1px;
    `;
    balloon.appendChild(string);

    container.appendChild(balloon);
  }

  setTimeout(() => container.remove(), 12000);
}

function triggerRainbowMode() {
  document.body.classList.add('party-background');

  const textElements = document.querySelectorAll('h1, .rainbow-target');
  textElements.forEach(el => el.classList.add('rainbow-text'));

  const container = document.querySelector('.container, main, body');
  if (container) container.classList.add('rainbow-border');

  createRainbowParticles();

  setTimeout(() => {
    document.body.classList.remove('party-background');
    textElements.forEach(el => el.classList.remove('rainbow-text'));
    if (container) container.classList.remove('rainbow-border');
  }, PARTY_DURATION_MS);
}

function createRainbowParticles() {
  if (isReducedMotion) return;
  const container = document.createElement('div');
  container.className = 'rainbow-particles-container';
  container.style.cssText = 'position: fixed; inset: 0; z-index: 998; pointer-events: none; overflow: hidden;';
  document.body.appendChild(container);

  const particleCount = window.innerWidth < 768 ? 8 : 15;

  for (let i = 0; i < particleCount; i++) {
    const particle = document.createElement('div');
    particle.className = 'rainbow-particle';

    const size = 20 + Math.random() * 30;
    const x = Math.random() * window.innerWidth;
    const duration = 6 + Math.random() * 4;
    const delay = Math.random() * 2;

    particle.style.cssText = `
      position: absolute; left: ${x}px; top: 100vh; width: ${size}px; height: ${size}px;
      background: linear-gradient(45deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #4b0082, #9400d3, #ff0000);
      background-size: 300% 300%; border-radius: 50%;
      animation: rainbow-particle-float ${duration}s ease-out forwards, rainbow-gradient ${duration / 2}s linear infinite;
      animation-delay: ${delay}s; box-shadow: 0 0 20px rgba(255, 255, 255, 0.8);
    `;
    container.appendChild(particle);
  }

  setTimeout(() => container.remove(), 10000);
}

// Rotating custom cursor (👉 with an occasional 🖕) for clicks outside the dossier.
function updateCursor() {
  const normalizedRotation = cursorRotation % 360;
  const cursorEmojis = ['👉', '👉', '👉', '👉', '🖕']; // 80% pointer, 20% rude
  const selectedEmoji = cursorEmojis[Math.floor(Math.random() * cursorEmojis.length)];
  const cursorSvg = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" style="font-size: 24px"><text y="24" transform="rotate(${normalizedRotation} 16 16)">${selectedEmoji}</text></svg>`;
  document.body.style.cursor = `url('${cursorSvg}') 4 4, auto`;
}

// ============================================================================
// VIDEO MODAL
// ============================================================================
const videoModal = document.getElementById('video-modal');
const videoModalBackdrop = document.getElementById('video-modal-backdrop');
const videoModalClose = document.getElementById('video-modal-close');
const videoModalPlayer = document.getElementById('video-modal-player');
const videoPlayBtn = document.getElementById('video-play-btn');

const MUX_VIDEO_URL = 'https://player.mux.com/o21k0000sIF4TQTGx02fpk3cdlJHLIm5WnzigUU4jEW18A?metadata-video-title=Chuckles&video-title=Chuckles&accent-color=%233a3a3a&loop=true&autoplay=true';

function openVideoModal() {
  if (!videoModal || !videoModalPlayer) return;

  discover('footage');
  stopAudio();
  const iframe = document.createElement('iframe');
  iframe.src = MUX_VIDEO_URL + (isMuted() ? '&muted=true' : '');
  iframe.title = 'Chuckles surveillance footage';
  iframe.allow = 'accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;';
  iframe.allowFullscreen = true;
  videoModalPlayer.innerHTML = '';
  videoModalPlayer.appendChild(iframe);

  videoModal.showModal();
  videoModal.classList.add('active');
  document.body.style.overflow = 'hidden';
  if (videoModalClose) videoModalClose.focus();
}

function closeVideoModal() {
  if (!videoModal || !videoModalPlayer) return;

  videoModal.classList.remove('active');
  if (videoModal.open) videoModal.close();
  document.body.style.overflow = '';
  videoModalPlayer.innerHTML = ''; // removing the iframe stops playback
  if (videoPlayBtn) videoPlayBtn.focus();
}

if (videoPlayBtn) videoPlayBtn.addEventListener('click', openVideoModal);
if (videoModalClose) videoModalClose.addEventListener('click', closeVideoModal);
if (videoModalBackdrop) videoModalBackdrop.addEventListener('click', closeVideoModal);

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && videoModal && videoModal.classList.contains('active')) {
    closeVideoModal();
  }
});

videoModal?.addEventListener('cancel', (event) => { event.preventDefault(); closeVideoModal(); });
